import React, { useState, useEffect } from 'react';
import { ArrowLeft, Eye, Flag, Users2, TrendingUp, DollarSign, Users, Download, Search, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { userManagementService, UserProfile, SortField, SortDirection } from '../../services/userManagementService';
import Header from '../../components/ui/Header';


const UserAnalyticsTable: React.FC = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ show: boolean; userId: string | null; userName: string }>({ 
    show: false, 
    userId: null, 
    userName: '' 
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [activateConfirmation, setActivateConfirmation] = useState<{ show: boolean; userId: string | null; userName: string }>({ 
    show: false, 
    userId: null, 
    userName: '' 
  });
  const [isActivating, setIsActivating] = useState(false);
  const [deactivateConfirmation, setDeactivateConfirmation] = useState<{ show: boolean; userId: string | null; userName: string }>({ 
    show: false, 
    userId: null, 
    userName: '' 
  });
  const [isDeactivating, setIsDeactivating] = useState(false);

  useEffect(() => {
    loadAllUsers();
  }, []);

  useEffect(() => {
    // Filter and sort users when search term or sort changes
    filterAndSortUsers();
  }, [users, searchTerm, sortField, sortDirection]);

  const loadAllUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all users without pagination
      const allUsers = await userManagementService.getAllUsersWithStats();
      setUsers(allUsers);
    } catch (err) {
      console.error('Error loading all users:', err);
      setError('Failed to load user analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortUsers = () => {
    let filtered = [...users];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.nationality?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.gender?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });

    setFilteredUsers(filtered);
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '⇅';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      setIsDeleting(true);
      await userManagementService.deleteUser(userId);
      
      // Remove user from local state
      setUsers(prevUsers => prevUsers.filter(u => u.id !== userId));
      setFilteredUsers(prevUsers => prevUsers.filter(u => u.id !== userId));
      
      // Close confirmation dialog
      setDeleteConfirmation({ show: false, userId: null, userName: '' });
      
      // Show success message (optional)
      alert('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteConfirmation = (userId: string, userName: string) => {
    setDeleteConfirmation({ show: true, userId, userName });
  };

  const openActivateConfirmation = (userId: string, userName: string) => {
    setActivateConfirmation({ show: true, userId, userName });
  };

  const openDeactivateConfirmation = (userId: string, userName: string) => {
    setDeactivateConfirmation({ show: true, userId, userName });
  };

  const handleActivateUser = async (userId: string) => {
    try {
      setIsActivating(true);
      const result = await userManagementService.activateUser(userId);
      
      if (result.success) {
        // Update user in local state
        setUsers(prevUsers => prevUsers.map(u => 
          u.id === userId 
            ? { ...u, isActivated: true } 
            : u
        ));
        setFilteredUsers(prevUsers => prevUsers.map(u => 
          u.id === userId 
            ? { ...u, isActivated: true } 
            : u
        ));
        
        // Close confirmation dialog
        setActivateConfirmation({ show: false, userId: null, userName: '' });
        
        // Show success message
        alert(result.message || 'User activated successfully');
      } else {
        alert(result.message || 'Failed to activate user');
      }
    } catch (error: any) {
      console.error('Error activating user:', error);
      alert(error.message || 'Failed to activate user. Please try again.');
    } finally {
      setIsActivating(false);
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    try {
      setIsDeactivating(true);
      const result = await userManagementService.deactivateUser(userId);
      
      if (result.success) {
        // Update user in local state
        setUsers(prevUsers => prevUsers.map(u => 
          u.id === userId 
            ? { ...u, isActivated: false } 
            : u
        ));
        setFilteredUsers(prevUsers => prevUsers.map(u => 
          u.id === userId 
            ? { ...u, isActivated: false } 
            : u
        ));
        
        // Close confirmation dialog
        setDeactivateConfirmation({ show: false, userId: null, userName: '' });
        
        // Show success message
        alert(result.message || 'User deactivated successfully');
      } else {
        alert(result.message || 'Failed to deactivate user');
      }
    } catch (error: any) {
      console.error('Error deactivating user:', error);
      alert(error.message || 'Failed to deactivate user. Please try again.');
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleExportUsers = () => {
    try {
      const csvContent = [
        ['Name', 'Email', 'Age', 'Nationality', 'Gender', 'Registration Date', 'Total Savings (€)', 'Avg Redemption Amount', 'Redemption Count', 'Last Activity Date'],
        ...filteredUsers.map(user => [
          user.fullName,
          user.email,
          user.age || 'N/A',
          user.nationality || 'N/A',
          user.gender || 'N/A',
          new Date(user.createdAt).toLocaleDateString(),
          `€${user.totalSavings.toFixed(2)}`,
          `€${user.avgRedemptionAmount.toFixed(2)}`,
          user.redemptionsCount.toString(),
          new Date(user.createdAt).toLocaleDateString()
        ])
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-analytics-complete-table-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting users:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  const handleSidebarCollapse = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading user analytics table...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️ Error</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={loadAllUsers}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Breadcrumb Navigation */}
      <div className="mb-6">
        <Link 
          to="/user-management"
          className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to User Management Dashboard
        </Link>
      </div>

      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Analytics Table</h1>
            <p className="mt-2 text-gray-600">
              Comprehensive tabular analysis of individual user profiles and detailed behavioral metrics
              <br />
              <span className="text-sm font-medium text-green-600">
                ✓ Complete dataset • {filteredUsers.length} of {users.length} users displayed • Infinite scroll enabled
              </span>
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            <button
              onClick={handleExportUsers}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Complete Table
            </button>
            
            <button
              onClick={loadAllUsers}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* Advanced Search and Filtering */}
      <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search users by name, email, nationality, or gender..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="ml-6 text-sm text-gray-600">
            Real-time filtering • Showing {filteredUsers.length} results
          </div>
        </div>
      </div>

      {/* Main User Analytics Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {/* Table Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Complete User Analytics Dataset
            </h3>
            <div className="text-sm text-gray-500">
              {filteredUsers.length} users • Click column headers to sort • Data loads progressively
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('fullName')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Name</span>
                    <span className="text-gray-400">{getSortIcon('fullName')}</span>
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('age')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Age</span>
                    <span className="text-gray-400">{getSortIcon('age')}</span>
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('nationality')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Nationality</span>
                    <span className="text-gray-400">{getSortIcon('nationality')}</span>
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('gender')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Gender</span>
                    <span className="text-gray-400">{getSortIcon('gender')}</span>
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Registration Date</span>
                    <span className="text-gray-400">{getSortIcon('createdAt')}</span>
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('totalSavings')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Total Savings (€)</span>
                    <span className="text-gray-400">{getSortIcon('totalSavings')}</span>
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('avgRedemptionAmount')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Avg Redemption Amount</span>
                    <span className="text-gray-400">{getSortIcon('avgRedemptionAmount')}</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Redemption Count</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Activity Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user, index) => (
                <tr 
                  key={user.id} 
                  className={`hover:opacity-90 transition-opacity ${
                    user.isActivated 
                      ? 'bg-green-50' :'bg-red-50'
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                          <span className="text-sm font-medium text-white">
                            {user.fullName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.fullName}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {user.age ? `${user.age} years` : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Flag className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">{user.nationality || 'Unknown'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Users2 className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900 capitalize">{user.gender || 'Unknown'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(user.createdAt)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <DollarSign className="h-4 w-4 text-green-500 mr-1" />
                      <span className="text-sm font-medium text-green-600">
                        €{user.totalSavings.toFixed(2)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <TrendingUp className="h-4 w-4 text-blue-500 mr-1" />
                      <span className="text-sm text-gray-900">
                        €{user.avgRedemptionAmount.toFixed(2)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Users className="h-4 w-4 text-purple-500 mr-1" />
                      <span className="text-sm font-medium text-gray-900">
                        {user.redemptionsCount}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(user.createdAt)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </button>
                      {!user.isActivated ? (
                        <button
                          onClick={() => openActivateConfirmation(user.id, user.fullName)}
                          className="text-green-600 hover:text-green-900 flex items-center"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Activate
                        </button>
                      ) : (
                        <button
                          onClick={() => openDeactivateConfirmation(user.id, user.fullName)}
                          className="text-orange-600 hover:text-orange-900 flex items-center"
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Deactivate
                        </button>
                      )}
                      <button
                        onClick={() => openDeleteConfirmation(user.id, user.fullName)}
                        className="text-red-600 hover:text-red-900 flex items-center"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search criteria to see more users.
            </p>
          </div>
        )}

        {/* Table Footer Summary */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Displaying <span className="font-medium">{filteredUsers.length}</span> of{' '}
              <span className="font-medium">{users.length}</span> total users
              {searchTerm && (
                <span className="ml-2 text-gray-500">
                  • Filtered by: "{searchTerm}"
                </span>
              )}
            </div>
            
            <div className="text-sm text-gray-500">
              Sorted by {sortField} ({sortDirection === 'asc' ? 'ascending' : 'descending'})
            </div>
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Current View Summary</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{filteredUsers.length}</div>
            <div className="text-sm text-gray-600">Users Displayed</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              €{filteredUsers.reduce((sum, u) => sum + u.totalSavings, 0).toFixed(0)}
            </div>
            <div className="text-sm text-gray-600">Total Savings</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {filteredUsers.reduce((sum, u) => sum + u.redemptionsCount, 0)}
            </div>
            <div className="text-sm text-gray-600">Total Redemptions</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {filteredUsers.length > 0 ? (filteredUsers.reduce((sum, u) => sum + (u.age || 0), 0) / filteredUsers.filter(u => u.age !== null).length).toFixed(1) : 0}
            </div>
            <div className="text-sm text-gray-600">Average Age</div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="ml-3 text-lg font-semibold text-gray-900">Delete User</h3>
            </div>
            
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete <strong>{deleteConfirmation.userName}</strong>? 
              This action cannot be undone and will permanently remove all user data including redemption history.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmation({ show: false, userId: null, userName: '' })}
                disabled={isDeleting}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteConfirmation.userId && handleDeleteUser(deleteConfirmation.userId)}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activate Confirmation Modal */}
      {activateConfirmation.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="ml-3 text-lg font-semibold text-gray-900">Activate User</h3>
            </div>
            
            <p className="text-gray-700 mb-6">
              Are you sure you want to activate <strong>{activateConfirmation.userName}</strong>? 
              The user will be able to redeem offers immediately after activation.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setActivateConfirmation({ show: false, userId: null, userName: '' })}
                disabled={isActivating}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => activateConfirmation.userId && handleActivateUser(activateConfirmation.userId)}
                disabled={isActivating}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center"
              >
                {isActivating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Activating...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Activate User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Confirmation Modal */}
      {deactivateConfirmation.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                <XCircle className="h-5 w-5 text-orange-600" />
              </div>
              <h3 className="ml-3 text-lg font-semibold text-gray-900">Deactivate User</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-3">
                Are you sure you want to deactivate <strong>{deactivateConfirmation.userName}</strong>?
              </p>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-orange-800">
                  <strong>⚠️ Important:</strong> Once deactivated, this user will need to activate their account again with a new QR code before they can redeem any offers.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeactivateConfirmation({ show: false, userId: null, userName: '' })}
                disabled={isDeactivating}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deactivateConfirmation.userId && handleDeactivateUser(deactivateConfirmation.userId)}
                disabled={isDeactivating}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center"
              >
                {isDeactivating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deactivating...
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 mr-2" />
                    Deactivate User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAnalyticsTable;